import React, { useState, useEffect, useRef } from "react";
import { apiCall } from "../Apis/apiCall";
import { authApi } from "../Apis/Api";
import { Icon } from "@iconify/react/dist/iconify.js";
import Breadcrumb from "../components/Breadcrumb";
import LoadingComponent from "../components/ui/LoadingComponent";
import { RECEIPT_IMG, TICKET_IMG } from "../utils/Contants";
import { toast } from "react-toastify";
import InfoModal from "../components/ui/InfoeModal"; // 👈 NEW: Import InfoModal

const MAX_FOOTER = 60;

// Layout Options (Frontend Labels)
const LAYOUT_OPTIONS = [
  { value: "img-top-both", label: "Image on top, Name below (Both)" },
  { value: "name-top-both", label: "Name on top, Image below (Both)" },
  { value: "img-top-logo", label: "Logo Only" },
  { value: "img-top-name", label: "Name Only" },
  { value: "side-by-side-both", label: "Side-by-side (Both)" },
];

// === MAPPING: Frontend to Backend ===
const mapLayoutToBackend = (value) => {
  const map = {
    "img-top-both": "image_top_both",
    "name-top-both": "name_top_both",
    "img-top-logo": "logo_only",
    "img-top-name": "name_only",
    "side-by-side-both": "side_by_side",
  };
  return map[value] || "image_top_both";
};

// === MAPPING: Backend to Frontend ===
const mapLayoutFromBackend = (value) => {
  const map = {
    "image_top_both": "img-top-both",
    "name_top_both": "name-top-both",
    "logo_only": "img-top-logo",
    "name_only": "img-top-name",
    "side_by_side": "side-by-side-both",
  };
  return map[value] || "img-top-both";
};

// Helper to extract layout and content
const getLayout = (value) => {
  if (value.includes("img")) return "img";
  if (value.includes("name-top")) return "name";
  if (value.includes("side")) return "side";
  return "img";
};

const getContent = (value) => {
  if (value.includes("both")) return "both";
  if (value.includes("logo")) return "logo";
  if (value.includes("name")) return "name";
  return "both";
};

// Build preview data with full URLs
const buildPreviewData = (data) => ({
  ...data,
  receipt_logo_url: data.receipt_logo_url
    ? `${RECEIPT_IMG}/${data.receipt_logo_url}`
    : "",
  ticket_logo_url: data.ticket_logo_url
    ? `${TICKET_IMG}/${data.ticket_logo_url}`
    : "",
  receipt_layout: data.receipt_layout || "img-top-both",
  ticket_layout: data.ticket_layout || "img-top-both",
});

const ReceiptUIPage = () => {
  const [templateData, setTemplateData] = useState({
    receipt_logo_url: "",
    receipt_company_name: "",
    receipt_footer_text: "",
    receipt_layout: "img-top-both",
    ticket_logo_url: "",
    ticket_company_name: "",
    ticket_footer_text: "",
    ticket_layout: "img-top-both",
  });

  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [receiptImageUploading, setReceiptImageUploading] = useState(false);
  const [ticketImageUploading, setTicketImageUploading] = useState(false);
  const [receiptImagePreview, setReceiptImagePreview] = useState("");
  const [ticketImagePreview, setTicketImagePreview] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false); // 👈 NEW: Info Modal State

  const previewSectionRef = useRef(null);

  /* ------------------- FETCH DATA ------------------- */
  useEffect(() => {
    fetchTemplateData();
  }, []);

  const fetchTemplateData = async () => {
    setLoading(true);
    try {
      const response = await apiCall("get", "/get-receipt-ticket-temp");
      if (response.data.success) {
        const data = response.data.data;

        const formattedPreview = buildPreviewData({
          ...data,
          receipt_logo_url: data.receipt_logo_url || "",
          ticket_logo_url: data.ticket_logo_url || "",
        });

        setPreviewData(formattedPreview);
        setTemplateData({
          receipt_logo_url: data.receipt_logo_url || "",
          receipt_company_name: data.receipt_company_name || "",
          receipt_footer_text: data.receipt_footer_text || "",
          receipt_layout: mapLayoutFromBackend(data.receipt_layout),

          ticket_logo_url: data.ticket_logo_url || "",
          ticket_company_name: data.ticket_company_name || "",
          ticket_footer_text: data.ticket_footer_text || "",
          ticket_layout: mapLayoutFromBackend(data.ticket_layout),
        });

        if (data.receipt_logo_url) {
          setReceiptImagePreview(`${RECEIPT_IMG}/${data.receipt_logo_url}`);
        }
        if (data.ticket_logo_url) {
          setTicketImagePreview(`${TICKET_IMG}/${data.ticket_logo_url}`);
        }
      }
    } catch (error) {
      console.error("Error fetching config:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  /* ------------------- INPUT CHANGE (LIVE PREVIEW) ------------------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes("footer_text") && value.length > MAX_FOOTER) return;

    setTemplateData((prev) => {
      const updated = { ...prev, [name]: value };
      setPreviewData(buildPreviewData(updated));
      return updated;
    });
  };

  /* ------------------- LAYOUT SELECTION HANDLER ------------------- */
  const handleLayoutChange = (type, value) => {
    setTemplateData((prev) => {
      const updated = { ...prev, [type]: value };
      setPreviewData(buildPreviewData(updated));
      return updated;
    });
  };

  /* ------------------- IMAGE UPLOAD ------------------- */
  const handleFileSelect = async (file, type) => {
    if (!file) {
      if (type === "receipt") {
        setReceiptImagePreview("");
        setTemplateData((prev) => ({ ...prev, receipt_logo_url: "" }));
        setPreviewData((prev) => ({ ...prev, receipt_logo_url: "" }));
      } else {
        setTicketImagePreview("");
        setTemplateData((prev) => ({ ...prev, ticket_logo_url: "" }));
        setPreviewData((prev) => ({ ...prev, ticket_logo_url: "" }));
      }
      return;
    }

    const localPreview = URL.createObjectURL(file);
    if (type === "receipt") {
      setReceiptImagePreview(localPreview);
      setReceiptImageUploading(true);
    } else {
      setTicketImagePreview(localPreview);
      setTicketImageUploading(true);
    }

    try {
      const uploadResponse = await authApi.Image_Upload(
        file,
        type === "receipt" ? "receipt_logo" : "ticket_logo"
      );
      const imageFilename = uploadResponse.tiny_url;

      if (type === "receipt") {
        setTemplateData((prev) => ({ ...prev, receipt_logo_url: imageFilename }));
        setReceiptImagePreview(`${RECEIPT_IMG}/${imageFilename}`);
        setPreviewData((prev) => ({
          ...prev,
          receipt_logo_url: `${RECEIPT_IMG}/${imageFilename}`,
        }));
      } else {
        setTemplateData((prev) => ({ ...prev, ticket_logo_url: imageFilename }));
        setTicketImagePreview(`${TICKET_IMG}/${imageFilename}`);
        setPreviewData((prev) => ({
          ...prev,
          ticket_logo_url: `${TICKET_IMG}/${imageFilename}`,
        }));
      }
      toast.success(`${type === "receipt" ? "Receipt" : "Ticket"} logo uploaded!`);
    } catch (err) {
      toast.error("Upload failed");
      if (type === "receipt") {
        setReceiptImagePreview("");
        setTemplateData((prev) => ({ ...prev, receipt_logo_url: "" }));
        setPreviewData((prev) => ({ ...prev, receipt_logo_url: "" }));
      } else {
        setTicketImagePreview("");
        setTemplateData((prev) => ({ ...prev, ticket_logo_url: "" }));
        setPreviewData((prev) => ({ ...prev, ticket_logo_url: "" }));
      }
    } finally {
      if (type === "receipt") setReceiptImageUploading(false);
      else setTicketImageUploading(false);
    }
  };

  const handleFileInputChange = (e, type) => {
    const file = e.target.files?.[0] ?? null;
    handleFileSelect(file, type);
    e.target.value = "";
  };

  /* ------------------- SAVE ONLY ------------------- */
  const handleSaveOnly = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    const payload = {
      receipt_logo_url: templateData.receipt_logo_url || "",
      receipt_company_name: templateData.receipt_company_name,
      receipt_footer_text: templateData.receipt_footer_text,
      receipt_layout: mapLayoutToBackend(templateData.receipt_layout),

      ticket_logo_url: templateData.ticket_logo_url || "",
      ticket_company_name: templateData.ticket_company_name,
      ticket_footer_text: templateData.ticket_footer_text,
      ticket_layout: mapLayoutToBackend(templateData.ticket_layout),
    };

    try {
      const response = await apiCall("put", "/update-receipt-ticket-temp", payload);
      if (response.data?.success) {
        const updated = response.data.data || templateData;
        const finalPreview = buildPreviewData({
          ...updated,
          receipt_logo_url: updated.receipt_logo || "",
          ticket_logo_url: updated.ticket_logo || "",
        });

        setPreviewData(finalPreview);
        toast.success(response.data.message || "Saved successfully!");
        await fetchTemplateData();
      }
    } catch (error) {
      console.error("Save error:", error.response?.data || error);
      toast.error(error.response?.data?.message || "Save failed");
    } finally {
      setSubmitLoading(false);
    }
  };

  /* ------------------- PREVIEW ONLY ------------------- */
  const handlePreviewOnly = () => {
    if (previewData) {
      setShowPreview(true);
      setTimeout(() => {
        previewSectionRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  // 👈 NEW: Info Modal Handlers
  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  /* ------------------- RENDER HEADER (Receipt Only) ------------------- */
  const renderHeader = (logoUrl, name, layoutValue) => {
    const layout = getLayout(layoutValue);
    const content = getContent(layoutValue);

    const showLogo = logoUrl && content !== "name";
    const showName = name && content !== "logo";

    if (!showLogo && !showName) return null;

    if (layout === "img") {
      return (
        <>
          {showLogo && (
            <img src={logoUrl} alt="logo" className="h-60-px object-fit-contain" />
          )}
          {showName && <h6 className="mt-2 font-weight-bold text-primary-light">{name}</h6>}
        </>
      );
    }

    if (layout === "name") {
      return (
        <>
          {showName && <h6 className="mb-2 font-weight-bold text-primary-light">{name}</h6>}
          {showLogo && (
            <img src={logoUrl} alt="logo" className="h-60-px object-fit-contain" />
          )}
        </>
      );
    }

    if (layout === "side") {
      return (
        <div className="d-flex align-items-center gap-3">
          {showLogo && (
            <img src={logoUrl} alt="logo" className="h-60-px object-fit-contain" />
          )}
          {showName && <h6 className="mb-0 font-weight-bold text-primary-light">{name}</h6>}
        </div>
      );
    }

    return null;
  };

  /* ------------------- RENDER TICKET HEADER (with QR Code) ------------------- */
  const renderTicketHeader = (logoUrl, name, layoutValue) => {
    const layout = getLayout(layoutValue);
    const content = getContent(layoutValue);

    const showLogo = logoUrl && content !== "name";
    const showName = name && content !== "logo";
    return (
      <div className="d-flex justify-content-between align-items-center mb-16">
        <div className="text-start">
          {(() => {
            if (layout === "img") {
              return (
                <>
                  {showLogo && (
                    <img src={logoUrl} alt="logo" className="h-60-px object-fit-contain" />
                  )}
                  {showName && <h6 className="mt-2 font-weight-bold text-primary-light">{name}</h6>}
                </>
              );
            }
            if (layout === "name") {
              return (
                <>
                  {showName && <h6 className="mb-2 font-weight-bold text-primary-light">{name}</h6>}
                  {showLogo && (
                    <img src={logoUrl} alt="logo" className="h-60-px object-fit-contain" />
                  )}
                </>
              );
            }
            if (layout === "side") {
              return (
                <div className="d-flex align-items-center gap-3">
                  {showLogo && (
                    <img src={logoUrl} alt="logo" className="h-60-px object-fit-contain" />
                  )}
                  {showName && <h6 className="mb-0 font-weight-bold text-primary-light">{name}</h6>}
                </div>
              );
            }
            return null;
          })()}
        </div>

        <div className="text-end">
          <div className="border rounded-4 p-2 d-inline-block shadow-4">
            <img
              src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=TIC-WTRUOG"
              alt="QR Code"
              className="w-80-px h-80-px"
            />
          </div>
        </div>
      </div>
    );
  };

  /* ------------------- MAIN RETURN ------------------- */
  return (
    <>
      <Breadcrumb title="Receipt & Ticket Settings" />

      <div className="container-fluid mt-3">
        <div className="card shadow-4 rounded-8 bg-base border">
          <div className="card-header border-bottom py-3 px-4">
            <div className="d-flex align-items-center justify-content-end me-3">
              {/* <h6 className="mb-0 text-primary-light">
                Receipt & Ticket Setup
              </h6> */}
              {/* 👈 NEW: Info Button */}
              <button
                type="button"
                className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Information about Receipt & Ticket Settings"
                onClick={handleInfoClick}
                aria-label="Page Information"
              >
                <Icon icon="lucide:info" className="menu-icon" />
              </button>
            </div>
          </div>

          <div className="card-body p-24">
            {loading ? (
              <div className="text-center py-5">
                <LoadingComponent />
              </div>
            ) : (
              <form>
                <div className="row gy-4">
                  {/* TICKET SECTION - NOW FIRST */}
                  <div className="col-md-6">
                    <div className="border rounded-8 p-24 h-100 shadow-4 bg-base">
                      <h5 className="font-md font-weight-bold mb-16 pb-8 border-bottom border-color d-flex align-items-center text-primary-light">
                        <Icon icon="mdi:ticket-confirmation" className="me-8 font-xl text-brand" />
                        Ticket Setup
                      </h5>

                      <div className="mb-24">
                        <label className="form-label mb-8 font-sm font-weight-semibold text-primary-light">
                          Header Layout & Content
                        </label>
                        <div className="d-flex flex-wrap gap-2">
                          {LAYOUT_OPTIONS.map((option) => (
                            <div
                              key={option.value}
                              className={`px-3 py-2 rounded-5 cursor-pointer border transition-2 font-sm font-weight-medium ${
                                templateData.ticket_layout === option.value
                                  ? "border-primary-600 bg-primary-600 text-base"
                                  : "border-neutral-300 bg-transparent text-secondary-light"
                              }`}
                              onClick={() => handleLayoutChange("ticket_layout", option.value)}
                            >
                              {option.label}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mb-24">
                        <label className="form-label mb-8 font-sm font-weight-semibold text-primary-light">
                          Ticket Logo
                        </label>
                        <div className="mb-16">
                          {ticketImagePreview ? (
                            <img
                              src={ticketImagePreview}
                              alt="Ticket Logo"
                              className="rounded-8"
                              style={{
                                width: 120,
                                height: 120,
                                objectFit: "contain",
                                border: "1px solid var(--border-color)",
                                backgroundColor: "var(--input-bg)",
                              }}
                              onError={(e) => {
                                e.target.src =
                                  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JTUc8L3RleHQ+PC9zdmc+";
                              }}
                            />
                          ) : (
                            <div className="rounded-8 d-flex align-items-center justify-content-center border input-bg w-120-px h-120-px">
                              <Icon icon="mdi:image-outline" className="font-xxl text-neutral-400" />
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          className="d-none"
                          accept=".png,.jpg,.jpeg,.webp"
                          disabled={ticketImageUploading}
                          id="ticket-logo-input"
                          onChange={(e) => handleFileInputChange(e, "ticket")}
                        />
                        <label
                          htmlFor="ticket-logo-input"
                          className="btn btn-sm rounded-6 font-sm font-weight-medium px-16 py-8 border border-primary-600 text-primary-600 bg-transparent"
                        >
                          {ticketImageUploading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-8"></span>
                              Uploading...
                            </>
                          ) : ticketImagePreview ? (
                            "Change Logo"
                          ) : (
                            "Upload Logo"
                          )}
                        </label>
                        <small className="d-block mt-8 text-neutral-500 font-xs">
                          Recommended: PNG, JPG, JPEG (Max 2MB)
                        </small>
                      </div>

                      <div className="mb-24">
                        <label className="form-label mb-8 font-sm font-weight-semibold text-primary-light">
                          Ticket Company Name
                        </label>
                        <div className="input-group rounded-6 overflow-hidden border input-bg">
                          <span className="input-group-text border-0 input-bg text-neutral-500">
                            <Icon icon="mdi:company" className="font-lg" />
                          </span>
                          <input
                            type="text"
                            className="form-control border-0 font-sm input-bg text-primary-light"
                            name="ticket_company_name"
                            value={templateData.ticket_company_name}
                            onChange={handleInputChange}
                            placeholder="Enter company name"
                          />
                        </div>
                      </div>

                      <div className="mb-0">
                        <label className="form-label mb-8 font-sm font-weight-semibold text-primary-light">
                          Ticket Footer Text
                        </label>
                        <div className="input-group rounded-6 overflow-hidden border input-bg">
                          <span className="input-group-text border-0 input-bg text-neutral-500">
                            <Icon icon="mdi:text" className="font-lg" />
                          </span>
                          <input
                            type="text"
                            className="form-control border-0 font-sm input-bg text-primary-light"
                            name="ticket_footer_text"
                            value={templateData.ticket_footer_text}
                            onChange={handleInputChange}
                            placeholder="Max 60 characters"
                            maxLength={MAX_FOOTER}
                          />
                        </div>
                        <small className="d-block mt-2 text-neutral-500">
                          {templateData.ticket_footer_text.length}/{MAX_FOOTER} characters
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* RECEIPT SECTION - NOW SECOND */}
                  <div className="col-md-6">
                    <div className="border rounded-8 p-24 h-100 shadow-4 bg-base">
                      <h5 className="font-md font-weight-bold mb-16 pb-8 border-bottom border-color d-flex align-items-center text-primary-light">
                        <Icon icon="mdi:receipt-text" className="me-8 font-xl text-brand" />
                        Receipt Setup
                      </h5>

                      <div className="mb-24">
                        <label className="form-label mb-8 font-sm font-weight-semibold text-primary-light">
                          Header Layout & Content
                        </label>
                        <div className="d-flex flex-wrap gap-2">
                          {LAYOUT_OPTIONS.map((option) => (
                            <div
                              key={option.value}
                              className={`px-3 py-2 rounded-5 cursor-pointer border transition-2 font-sm font-weight-medium ${
                                templateData.receipt_layout === option.value
                                  ? "border-primary-600 bg-primary-600 text-base"
                                  : "border-neutral-300 bg-transparent text-secondary-light"
                              }`}
                              onClick={() => handleLayoutChange("receipt_layout", option.value)}
                            >
                              {option.label}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mb-24">
                        <label className="form-label mb-8 font-sm font-weight-semibold text-primary-light">
                          Receipt Logo
                        </label>
                        <div className="mb-16">
                          {receiptImagePreview ? (
                            <img
                              src={receiptImagePreview}
                              alt="Receipt Logo"
                              className="rounded-8"
                              style={{
                                width: 120,
                                height: 120,
                                objectFit: "contain",
                                border: "1px solid var(--border-color)",
                                backgroundColor: "var(--input-bg)",
                              }}
                              onError={(e) => {
                                e.target.src =
                                  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JTUc8L3RleHQ+PC9zdmc+";
                              }}
                            />
                          ) : (
                            <div className="rounded-8 d-flex align-items-center justify-content-center border input-bg w-120-px h-120-px">
                              <Icon icon="mdi:image-outline" className="font-xxl text-neutral-400" />
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          className="d-none"
                          accept=".png,.jpg,.jpeg,.webp"
                          disabled={receiptImageUploading}
                          id="receipt-logo-input"
                          onChange={(e) => handleFileInputChange(e, "receipt")}
                        />
                        <label
                          htmlFor="receipt-logo-input"
                          className="btn btn-sm rounded-6 font-sm font-weight-medium px-16 py-8 border border-primary-600 text-primary-600 bg-transparent"
                        >
                          {receiptImageUploading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-8"></span>
                              Uploading...
                            </>
                          ) : receiptImagePreview ? (
                            "Change Logo"
                          ) : (
                            "Upload Logo"
                          )}
                        </label>
                        <small className="d-block mt-8 text-neutral-500 font-xs">
                          Recommended: PNG, JPG, JPEG (Max 2MB)
                        </small>
                      </div>

                      <div className="mb-24">
                        <label className="form-label mb-8 font-sm font-weight-semibold text-primary-light">
                          Receipt Company Name
                        </label>
                        <div className="input-group rounded-6 overflow-hidden border input-bg">
                          <span className="input-group-text border-0 input-bg text-neutral-500">
                            <Icon icon="mdi:company" className="font-lg" />
                          </span>
                          <input
                            type="text"
                            className="form-control border-0 font-sm input-bg text-primary-light"
                            name="receipt_company_name"
                            value={templateData.receipt_company_name}
                            onChange={handleInputChange}
                            placeholder="Enter company name"
                          />
                        </div>
                      </div>

                      <div className="mb-0">
                        <label className="form-label mb-8 font-sm font-weight-semibold text-primary-light">
                          Receipt Footer Text
                        </label>
                        <div className="input-group rounded-6 overflow-hidden border input-bg">
                          <span className="input-group-text border-0 input-bg text-neutral-500">
                            <Icon icon="mdi:text" className="font-lg" />
                          </span>
                          <input
                            type="text"
                            className="form-control border-0 font-sm input-bg text-primary-light"
                            name="receipt_footer_text"
                            value={templateData.receipt_footer_text}
                            onChange={handleInputChange}
                            placeholder="Max 60 characters"
                            maxLength={MAX_FOOTER}
                          />
                        </div>
                        <small className="d-block mt-2 text-neutral-500">
                          {templateData.receipt_footer_text.length}/{MAX_FOOTER} characters
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-center gap-3 mt-32">
                  <button
                    type="button"
                    onClick={() => window.history.back()}
                    className="btn border border-danger-600 bg-hover-danger-200 text-danger-600 text-md radius-8 px-20 py-11"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={handleSaveOnly}
                    className="btn bg-primary-600 border border-primary-600 text-base text-md px-56 py-12 radius-8"
                    disabled={submitLoading}
                  >
                    {submitLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-8"></span>
                        Please wait...
                      </>
                    ) : (
                      "Save"
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handlePreviewOnly}
                    className="btn border border-primary-600 text-primary-600 bg-transparent text-md px-56 py-12 radius-8"
                    disabled={submitLoading || !previewData}
                  >
                    Preview
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Preview Section */}
      {showPreview && previewData && (
        <div className="container-fluid mt-32" ref={previewSectionRef}>
          <h5 className="text-center mb-24 font-lg font-weight-bold text-primary-light">
            Preview Receipt & Ticket
          </h5>

          <div className="row gy-4 justify-content-center">
            {/* Ticket Preview - NOW FIRST */}
            <div className="col-md-6 col-lg-4">
              <div className="border rounded-8 p-24 shadow-4 bg-base" style={{ maxWidth: "320px", margin: "0 auto" }}>
                <div className="mb-16">
                  {renderTicketHeader(
                    previewData.ticket_logo_url,
                    previewData.ticket_company_name || "PARK EASE VALET",
                    previewData.ticket_layout
                  )}
                </div>

                <div className="border-top border-color pt-16">
                  <div className="text-center mb-12">
                    <strong className="font-lg text-primary-light">TIC-WTRUOG</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Customer Name:</span>
                    <span className="font-weight-medium text-primary-light">Brace Young</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Phone:</span>
                    <span className="font-weight-medium text-primary-light">+92 xxxxxxxx</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Entry Time:</span>
                    <span className="font-weight-medium text-primary-light"> 05:39 PM</span>
                  </div>
                  <div className="my-3 border-top border-dashed" />
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Plate Number:</span>
                    <span className="font-weight-medium text-primary-light">K 54321</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Type:</span>
                    <span className="font-weight-medium text-primary-light">Electric Car</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Make:</span>
                    <span className="font-weight-medium text-primary-light">Vespa</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Model:</span>
                    <span className="font-weight-medium text-primary-light">Sprint 50</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Color:</span>
                    <span className="font-weight-medium text-primary-light">navy-blue</span>
                  </div>
                </div>

                <div className="my-12 pt-12 text-center border-top border-color">
                  <p className="font-xs mb-0 text-secondary-light">
                    {previewData.ticket_footer_text || "Please bring this ticket during checkout."}
                  </p>
                </div>
                <div className="text-center mt-16 font-xs text-neutral-500">
                  Powered by <strong className="text-primary-light">PARK EASE VALET</strong>
                </div>
              </div>
            </div>

            {/* Receipt Preview - NOW SECOND */}
            <div className="col-md-6 col-lg-4">
              <div className="border rounded-8 p-24 shadow-4 bg-base" style={{ maxWidth: "320px", margin: "0 auto" }}>
                <div className="text-center mb-16">
                  {renderHeader(
                    previewData.receipt_logo_url,
                    previewData.receipt_company_name || "PARK EASE VALET",
                    previewData.receipt_layout
                  )}
                </div>

                <div className="border-top border-color pt-16">
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Receipt #:</span>
                    <span className="font-weight-medium text-primary-light">REC-124-020</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Date:</span>
                    <span className="font-weight-medium text-primary-light">11/11/2025</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Time:</span>
                    <span className="font-weight-medium text-primary-light">06:36 PM</span>
                  </div>
                  <div className="my-3 border-top border-dashed" />
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Ticket #:</span>
                    <span className="font-weight-medium text-primary-light">TIC-WTRUOG</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Plate Number:</span>
                    <span className="font-weight-medium text-primary-light">K 54321</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Vehicle Type:</span>
                    <span className="font-weight-medium text-primary-light">Electric Car</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Make:</span>
                    <span className="font-weight-medium text-primary-light">Vespa</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Model:</span>
                    <span className="font-weight-medium text-primary-light">Sprint 50</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="font-xs text-secondary-light">Color:</span>
                    <span className="font-weight-medium text-primary-light">navy-blue</span>
                  </div>
                  <div className="my-3 border-top border-dashed" />
                  <div className="d-flex justify-content-between mb-4">
                    <span className="font-xs text-secondary-light">Vat Amount:</span>
                    <span className="font-weight-medium text-primary-light">0.00 PKR</span>
                  </div>
                  <div className="d-flex justify-content-between mb-4">
                    <span className="font-xs text-secondary-light">Parking Fee:</span>
                    <span className="font-weight-medium text-primary-light">0.00 PKR</span>
                  </div>
                  <div className="d-flex justify-content-between mb-4">
                    <span className="font-xs text-secondary-light">Total Tax:</span>
                    <span className="font-weight-medium text-primary-light">4.22 PKR</span>
                  </div>
                  <div className="d-flex justify-content-between mb-4">
                    <span className="font-xs text-secondary-light">Admin (1%):</span>
                    <span className="font-weight-medium text-primary-light">0.89 PKR</span>
                  </div>
                  <div className="d-flex justify-content-between mb-4">
                    <span className="font-xs text-secondary-light">FOT (5%):</span>
                    <span className="font-weight-medium text-primary-light">1.27 PKR</span>
                  </div>
                  <div className="my-12 pt-8 border-top border-color">
                    <div className="d-flex justify-content-between">
                      <span className="font-weight-bold text-primary-light">TOTAL:</span>
                      <span className="font-weight-bold text-brand">0.00 PKR</span>
                    </div>
                  </div>
                </div>

                <div className="my-12 pt-12 text-center border-top border-color">
                  <p className="font-xs mb-0 text-secondary-light">
                    {previewData.receipt_footer_text || "Thank you for parking with us. Have a safe journey!"}
                  </p>
                </div>
                <div className="text-center mt-16 font-xs text-neutral-500">
                  Powered by <strong className="text-primary-light">PARK EASE VALET</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-24">
            <button
              type="button"
              className="btn btn-sm px-24 border border-primary-600 text-primary-600 bg-transparent"
              onClick={() => setShowPreview(false)}
            >
              Close Preview
            </button>
          </div>
        </div>
      )}

      {/* 👈 NEW: Info Modal - EXACTLY SAME STYLE AS WorkFlowAssignList */}
      <InfoModal
        isOpen={showInfoModal}
        onClose={handleInfoClose}
        title="Receipt & Ticket Settings - Complete Guide"
      >
        <p className="text-neutral-900 mb-4">
          This page is your <strong>central control panel</strong> for customizing how your <strong>Receipts</strong> and <strong>Tickets</strong> look when printed for customers. 
          Everything you set here appears exactly as shown on physical/digital receipts and tickets.
        </p>

        <h6 className="fw-bold text-primary-600 mb-3">What You Can Customize</h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item"><strong>Header Layout</strong> – Choose how logo and company name appear together</li>
          <li className="list-group-item"><strong>Company Logo</strong> – Upload your brand logo for professional look</li>
          <li className="list-group-item"><strong>Company Name</strong> – Your business name as it should appear</li>
          <li className="list-group-item"><strong>Footer Message</strong> – Custom thank you note or instructions (max 60 chars)</li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">Layout Options Explained</h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item"><strong>Image on top, Name below</strong> – Classic professional look</li>
          <li className="list-group-item"><strong>Name on top, Image below</strong> – Brand name emphasized</li>
          <li className="list-group-item"><strong>Logo Only</strong> – Just your logo, no text</li>
          <li className="list-group-item"><strong>Name Only</strong> – Just company name, no logo</li>
          <li className="list-group-item"><strong>Side-by-side</strong> – Modern inline layout</li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">Ticket vs Receipt - Key Differences</h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>Ticket</strong><br/>
            • Given to customer when vehicle enters<br/>
            • Contains QR code for quick scanning<br/>
            • Shows entry details and vehicle info<br/>
            • Used for retrieval and checkout
          </li>
          <li className="list-group-item">
            <strong>Receipt</strong><br/>
            • Given during checkout/payment<br/>
            • Shows detailed pricing breakdown<br/>
            • Includes taxes, fees, and total amount<br/>
            • Serves as payment proof
          </li>
        </ul>

        <div className="alert alert-info small mt-4">
          <strong>Pro Tips:</strong><br/>
          • Use high-quality logos for best print results<br/>
          • Keep footer messages short and customer-friendly<br/>
          • Preview changes before saving to see exact results<br/>
          • Tickets auto-generate QR codes – no setup needed<br/>
          • Receipts automatically calculate and show tax breakdowns
        </div>

        <p className="text-neutral-900 mt-4 fw-bold text-primary">
          Remember: Professional receipts/tickets = Better customer experience + Trust in your service!
        </p>
        <p className="text-neutral-900 small">
          Business Impact: Clear branding builds recognition, detailed receipts prevent disputes, and well-designed tickets ensure smooth operations.
        </p>
      </InfoModal>
    </>
  );
};

export default ReceiptUIPage;